import { FC, ReactNode, useState } from 'react';
import { Box, Checkbox, FormControl, FormHelperText, ListSubheader, MenuItem, Select, SelectChangeEvent, Theme } from '@mui/material';

import SearchField from './SearchField';
import { bm } from '@/theme/bausteinMasse';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Phase 4a):
 * Einheitlicher Filter-Select. options-Prop rendert MenuItems.
 * placeholderOption fuer "Alle …"-Standardwert (leerer/None-Filter).
 *
 * 32px Höhe, 6px Radius, 13px Schrift, divider-Border (statt
 * Hardcode-#eee wie in den Bestand-Filtern).
 *
 * Feinschliff 1: optionales `searchable` — Suchfeld oben im
 * aufgeklappten Menü (client-seitiges Label-Filtering). Die
 * Select-Basis und der geschlossene Zustand bleiben unverändert;
 * Default an bei >10 Optionen. placeholderOption bleibt beim
 * Filtern immer sichtbar (Reset-Weg).
 *
 * 03.08.2026 — `error` / `helperText` / `fullWidth` (Entscheid Alex,
 * Option a). Anlass: der Buchungsdialog konnte nicht auf FilterSelect
 * umgestellt werden, weil das Meeting-Typ-Feld ein Pflichtfeld MIT
 * Fehlermeldung ist und alle Felder dort `fullWidth` stehen. Das
 * Design-System verlangt für genau diesen Fall die Erweiterung der
 * kanonischen Komponente statt einer Parallelvariante
 * (DESIGN_SYSTEM.md „Prozess & Verantwortlichkeit").
 *
 * STRIKT ADDITIV: ohne die drei neuen Props verhält sich die Komponente
 * Zeichen für Zeichen wie vorher. Der umschließende `FormControl`
 * entsteht nur, wenn `error` oder `helperText` gesetzt ist — sonst
 * bleibt das `Select` das äußere Element, damit die 32 bestehenden
 * Aufrufer ihr Layout (Breite, Flex-Verhalten) unverändert behalten.
 *
 * 04.08.2026 — `multiple` (Entscheid Alex, gebundene Telefonisten
 * Variante B). Anlass: die Berater-Mehrfachauswahl am Telefonisten-User
 * braucht ein Dropdown mit mehreren Werten. Decision #116 verbietet die
 * Parallelkomponente, also wird die kanonische erweitert — und zwar so,
 * dass das GESCHLOSSENE Feld aussieht wie jedes andere Dropdown im
 * System: 32px, gewählte Labels als Text, keine Chips (die sprengen die
 * Höhe). Die Haken stehen ausschließlich im offenen Menü.
 *
 * Auch hier strikt additiv: ohne `multiple` ist jede Zeile unterhalb
 * unverändert, und die Props-Union (s.u.) lässt die bestehenden
 * Aufrufer typseitig unangetastet.
 */
export interface FilterSelectOption {
  value: string | number;
  label: string;
  /**
   * Option sichtbar, aber nicht waehlbar (12.09.2026, BER-67 Design-
   * Angleichung). Anlass: die Tag/Monat/Jahr-Dropdowns des Antrags-Wizards
   * sperren Werte ausserhalb des zulaessigen Fensters — als rohes
   * `<TextField select>` per `MenuItem disabled`. Die Umstellung auf die
   * Hauskomponente (Decision #116) haette diese Sperre sonst verloren; ein
   * Ausfiltern waere keine Loesung, weil ein bereits gewaehlter, inzwischen
   * gesperrter Wert dann nicht mehr angezeigt wuerde.
   *
   * STRIKT ADDITIV: ohne das Feld rendert das MenuItem wie vorher.
   */
  disabled?: boolean;
}

interface FilterSelectBaseProps {
  options: FilterSelectOption[];
  placeholderOption?: FilterSelectOption;
  minWidth?: number;
  disabled?: boolean;
  searchable?: boolean;
  'aria-label'?: string;
  /** Fehlerzustand — roter Rahmen, und `helperText` wird rot gerendert. */
  error?: boolean;
  /**
   * Text unter dem Feld. Trägt bei `error` die Fehlermeldung, sonst einen
   * Hinweis. Erzeugt einen umschließenden `FormControl` — ohne diese Prop
   * (und ohne `error`) bleibt das Markup unverändert.
   */
  helperText?: string;
  /** Volle Breite des Elternelements — für Formular-/Dialog-Kontexte. */
  fullWidth?: boolean;
  /**
   * Optionaler Zusatz hinter dem Label EINER Option, nur im offenen Menü
   * (Alex 10.09.2026, Berater-Filter der Meetings-Liste).
   *
   * Anlass: der Filter zeigt seit dem 10.09. auch deaktivierte Berater, und
   * die müssen als solche erkennbar sein. `label` ist ein String und kann
   * keinen Badge tragen; eine zweite Dropdown-Komponente verbietet
   * Decision #116. Also erhält die kanonische Komponente einen Haken, an dem
   * der Aufrufer eine Kennzeichnung hängt.
   *
   * STRIKT ADDITIV: ohne diese Prop rendert das MenuItem Zeichen für Zeichen
   * wie vorher. Der GESCHLOSSENE Zustand bleibt in jedem Fall unberührt — er
   * zeigt weiterhin nur `label`, damit die Feldhöhe von 32px hält.
   */
  optionAdornment?: (option: FilterSelectOption) => ReactNode;
  /**
   * Menü beim Einhängen sofort offen (23.09.2026, #211-Erweiterung, Entscheid
   * Alex). Anlass: im Termine-Reiter des Falls ist die Terminart ein Badge;
   * ein Klick darauf soll direkt die erlaubten Ziele zeigen, nicht erst ein
   * geschlossenes Feld, das man ein zweites Mal anklicken muss. Eine zweite
   * Dropdown-Komponente verbietet Decision #116 — also bekommt die kanonische
   * einen Haken.
   *
   * STRIKT ADDITIV: ohne die Prop bleibt das Menü zu, bis jemand klickt.
   */
  defaultOpen?: boolean;
  /**
   * Menü wurde geschlossen — mit oder ohne Auswahl (Escape, Klick daneben).
   * Der Aufrufer braucht das, um ein nur temporär eingeblendetes Feld wieder
   * auszublenden. STRIKT ADDITIV: ohne die Prop passiert beim Schließen genau
   * das, was vorher passierte.
   */
  onClose?: () => void;
}

/**
 * Bewusst eine DISKRIMINIERTE UNION statt einer verbreiterten `value`-Prop.
 * Würde `value`/`onChange` einfach auf `T | T[]` erweitert, wäre unter
 * `strictFunctionTypes` kein einziger der bestehenden Aufrufer mehr
 * zuweisbar: deren Handler nimmt `string | number`, und Funktionsparameter
 * werden kontravariant geprüft. Die Union lässt den Einfach-Fall Zeichen für
 * Zeichen so, wie er war, und gibt der Mehrfachauswahl eigene Typen.
 */
export type FilterSelectProps =
  | (FilterSelectBaseProps & {
      multiple?: false;
      value: string | number;
      onChange: (value: string | number) => void;
    })
  | (FilterSelectBaseProps & {
      multiple: true;
      value: Array<string | number>;
      onChange: (value: Array<string | number>) => void;
    });

const FilterSelect: FC<FilterSelectProps> = ({
  value,
  onChange,
  options,
  placeholderOption,
  minWidth,
  disabled = false,
  searchable,
  'aria-label': ariaLabel,
  error = false,
  helperText,
  fullWidth = false,
  multiple = false,
  optionAdornment,
  defaultOpen,
  onClose,
}) => {
  const [searchText, setSearchText] = useState('');

  const allOptions = placeholderOption ? [placeholderOption, ...options] : options;
  const isSearchable = searchable ?? options.length > 10;

  const query = searchText.trim().toLowerCase();
  const filteredOptions = isSearchable && query
    ? options.filter((opt) => opt.label.toLowerCase().includes(query))
    : options;
  // Bei Mehrfachauswahl ist `placeholderOption` reiner Anzeigetext für den
  // leeren Zustand und NIE ein MenuItem — als Eintrag landete sie sonst als
  // echter Wert in der Auswahl-Liste.
  const withPlaceholder = (opts: FilterSelectOption[]) =>
    placeholderOption && !multiple ? [placeholderOption, ...opts] : opts;
  const visibleOptions = isSearchable
    ? withPlaceholder(filteredOptions)
    : withPlaceholder(options);

  const selectedValues = multiple ? (value as Array<string | number>) : [];

  /** Gewählte Labels, Reihenfolge wie in `options` — stabil, nicht klickabhängig. */
  const renderMultiple = () => {
    if (selectedValues.length === 0) return placeholderOption?.label ?? '';

    return options
      .filter((opt) => selectedValues.some((v) => `${v}` === `${opt.value}`))
      .map((opt) => opt.label)
      .join(', ');
  };

  const select = (
    <Select
      size="small"
      error={error}
      fullWidth={fullWidth}
      multiple={multiple}
      value={value}
      onChange={(e: SelectChangeEvent<unknown>) => {
        if (multiple) {
          // MUI liefert bei `multiple` immer ein Array; der String-Zweig ist
          // nur der dokumentierte Autofill-Fall.
          const raw = e.target.value;
          (onChange as (v: Array<string | number>) => void)(
            typeof raw === 'string' ? raw.split(',') : (raw as Array<string | number>),
          );
          return;
        }
        (onChange as (v: string | number) => void)(e.target.value as string | number);
      }}
      displayEmpty={!!placeholderOption}
      disabled={disabled}
      inputProps={{ 'aria-label': ariaLabel }}
      // Nur im searchable-Fall: Anzeige des gewaehlten Werts unabhaengig
      // von den (gefilterten) MenuItems aufloesen, sonst zeigt das Select
      // nichts an, wenn die gewaehlte Option gerade weggefiltert ist.
      // Bei Mehrfachauswahl IMMER selbst rendern — MUIs Default fuegt die
      // rohen Werte (IDs) zusammen statt der Labels.
      renderValue={
        multiple
          ? renderMultiple
          : isSearchable
            ? (v) => allOptions.find((opt) => `${opt.value}` === `${v}`)?.label ?? ''
            : undefined
      }
      // autoFocus:false — sonst zieht das Menue den Fokus vom Suchfeld ab.
      MenuProps={isSearchable ? { autoFocus: false } : undefined}
      onClose={isSearchable || onClose
        ? () => {
          if (isSearchable) setSearchText('');
          onClose?.();
        }
        : undefined}
      defaultOpen={defaultOpen}
      sx={{
        minWidth,
        // Masse aus `theme.bausteinMasse.eingabe` (Alt-Theme der Strecke: Original).
        fontSize: (t: Theme) => bm(t).eingabe.schrift,
        bgcolor: 'background.paper',
        // Beim Select liegt die sx-Klasse direkt auf dem
        // OutlinedInput-Root — der Theme-Override (theme/input.tsx,
        // 44px via :not(.MuiInputBase-multiline), 16px auf dem Input)
        // hat dort (0,2,0). `&.MuiOutlinedInput-root` hebt die
        // eigenen Regeln um eine Klasse an — ohne !important.
        '&.MuiOutlinedInput-root': {
          borderRadius: (t: Theme) => bm(t).eingabe.radius,
          '&:not(.MuiInputBase-multiline)': { height: (t: Theme) => bm(t).eingabe.hoehe },
          '& .MuiSelect-select': {
            py: 0,
            px: '10px',
            fontSize: (t: Theme) => bm(t).eingabe.schrift,
            color: (t: Theme) => bm(t).eingabe.wertFarbe,
          },
        },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
        '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: (t) => `${t.palette.text.primary}3D`,
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
          borderWidth: 1,
        },
      }}
    >
      {isSearchable && (
        <ListSubheader sx={{ px: 1, py: 0.75, lineHeight: 'normal', bgcolor: 'background.paper' }}>
          <SearchField
            value={searchText}
            onChange={setSearchText}
            placeholder="Suchen..."
            autoFocus
            width="100%"
            aria-label="Optionen durchsuchen"
            onKeyDown={(e) => {
              // Select-Typeahead/Selektion nicht ausloesen; Escape
              // laeuft durch und schliesst das Menue wie gewohnt.
              if (e.key !== 'Escape') e.stopPropagation();
            }}
          />
        </ListSubheader>
      )}
      {visibleOptions.map((opt) => (
        <MenuItem key={`${opt.value}`} value={opt.value} disabled={opt.disabled} sx={{ fontSize: (t: Theme) => bm(t).eingabe.schrift }}>
          {/* Nur im Mehrfach-Modus: ohne Haken ist im offenen Menue nicht
              erkennbar, was gewaehlt ist. Der GESCHLOSSENE Zustand bleibt
              davon unberuehrt und sieht aus wie jedes andere Dropdown. */}
          {multiple && (
            <Checkbox
              size="small"
              checked={selectedValues.some((v) => `${v}` === `${opt.value}`)}
              sx={{ p: 0, mr: 1 }}
            />
          )}
          {opt.label}
          {/* Der Zusatz sitzt HINTER dem Label und nur hier im Menue — im
              geschlossenen Feld wuerde er die Hausmasse sprengen. */}
          {optionAdornment && (
            <Box component="span" sx={{ display: 'inline-flex', ml: 1 }}>
              {optionAdornment(opt)}
            </Box>
          )}
        </MenuItem>
      ))}
      {isSearchable && query && filteredOptions.length === 0 && (
        <MenuItem disabled sx={{ fontSize: (t: Theme) => bm(t).eingabe.schrift }}>
          Keine Treffer
        </MenuItem>
      )}
    </Select>
  );

  // Ohne Fehlerzustand und ohne Hilfetext bleibt das Select das aeussere
  // Element — genau wie vor der Erweiterung. Ein bedingungsloser FormControl
  // haette allen 32 Bestands-Aufrufern eine zusaetzliche Box untergeschoben
  // und damit Breite und Flex-Verhalten veraendert; das waere keine additive
  // Erweiterung mehr, sondern ein stiller Umbau.
  if (!helperText && !error) {
    return select;
  }

  return (
    // `variant="outlined"` ist PFLICHT, nicht Kosmetik: der FormControl gibt
    // seine Variante per Kontext an das Select weiter. Mit dem MUI-Default
    // (`standard`) rendert es als `MuiInput-underline` statt
    // `MuiOutlinedInput-root` — Unterstrich statt Rahmen, und die 32px/6px aus
    // dem sx greifen ins Leere, weil sie an `.MuiOutlinedInput-root` haengen.
    // Beim Bau gemessen, nicht vermutet: der Fehlerzustand kam als
    // Standard-Variante heraus.
    <FormControl error={error} fullWidth={fullWidth} variant="outlined" sx={{ minWidth }}>
      {select}
      {helperText && (
        // 11px = Meta/Sub aus DESIGN_SYSTEM Teil B, `ml: 0` weil MUI den
        // Hilfetext sonst um 14px einrueckt und er unter dem Feld verspringt.
        <FormHelperText sx={{ fontSize: (t: Theme) => bm(t).eingabe.hinweis, mt: 0.5, ml: 0 }}>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FilterSelect;
